import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { Item } from '../content/types.ts';

import { inEncounter } from '../domain/runtime.ts';
import { encounterActionProblem } from '../rules/encounter.ts';
import { deriveStats } from './derivedStats.ts';
import { spendEncounterAction } from './encounterService.ts';
import type { ConditionContent } from '../content/conditions.ts';
import { applyCondition } from './conditionService.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import { handAllocationField, inHandCopies } from '../rules/inventory.ts';
import type { Effect } from '../rules/effects.ts';
import {
  POTION_SICKNESS_CONDITION_ID,
  POTION_SICKNESS_SECONDS,
  planItemUse,
  startedPotionCadence,
  physicalUseContext,
} from '../rules/itemUse.ts';
import type { ItemUsePlan } from '../rules/itemUse.ts';

/**
 * Using a consumable Item.
 *
 * PURE-THEN-PERSIST, the E1 shape. This works out every record the use would
 * change and returns them; the caller commits them together through the
 * existing checkpoint machinery. It writes nothing itself, so a refusal cannot
 * leave anything half-applied.
 *
 * SCOPE. This is the consumable pathway and nothing else. Readying and
 * swapping live in `hotlistUseService` (E5) and spend their own Action; this
 * one spends the consumable arm's. No generic Use a
 * Hotlist Item Action, no Retrieve, no scrolls, no Spells, no Interrupt
 * interface. Rules p.49 says healing potions may also be consumed as an
 * Interrupt - recorded, not built.
 */

export type ItemUseOutcome =
  | {
      ok: true;
      character: Character;
      runtime: RuntimeState;
      /** For the sheet to report what happened, in order. */
      lines: string[];
      /** True when this use inflicted Potion Sickness. */
      potionSickness: boolean;
    }
  | { ok: false; reason: string };

export interface ItemUseDeps {
  catalogue: InventoryCatalogue;
  conditions: ConditionContent;
  effects: readonly Effect[];
  newId: () => string;
}

/** Where the copy is taken from. EXPLICIT, so the service can never confuse a
 *  prepared Hotlist potion with one still buried in the Inventory. */
/**
 * Which physical allocation supplies the consumed copy. RULE DCC-POT-003.
 *
 * The route decides WHERE the copy comes from and nothing else. The effect,
 * the Action, the cadence and Potion Sickness are identical on all three,
 * because they are the same drink.
 */
export type UseRoute = 'hotlist' | 'inventory' | 'hand';

// `availableCopies` and `hotlistedCopies` used to live here. They are now
// `physicalUseContext` in the rules layer, because three copies of the same
// arithmetic is how the Play Screen's warning preflight went stale when `held`
// arrived.

/**
 * Potion Sickness on this runtime (RULE DCC-POT-004, Rules p.49).
 *
 * SHARED by the printed Potion use below and by Item Powers that are Potions,
 * so there is one implementation of "Poisoned for fifteen seconds".
 */
export function inflictPotionSickness(
  runtime: RuntimeState,
  deps: Pick<ItemUseDeps, 'conditions' | 'newId'>,
): { ok: true; runtime: RuntimeState; line: string } | { ok: false; reason: string } {
  const sickness = applyCondition(runtime, POTION_SICKNESS_CONDITION_ID, deps.newId, deps.conditions);
  if (!sickness.ok) return { ok: false, reason: sickness.reason };

  // The catalogue's Poisoned ends "until treated with an antidote" (Rules
  // p.45). Potion Sickness ends after 15 seconds (Rules p.49), so THIS
  // INSTANCE gets the printed elapsed-time ending instead - only the record
  // just created, leaving any ordinary Poison the crawler already had exactly
  // as it was. Its recurring damage and its stacking still come from the
  // shared condition definition; none of that is re-implemented here.
  const added = sickness.runtime.active[sickness.runtime.active.length - 1];
  return {
    ok: true,
    runtime: {
      ...sickness.runtime,
      active: sickness.runtime.active.map((record) =>
        record === added
          ? // `endsWhen` on the RECORD is what `advanceTime` reads. The
            // `effect` is the shared catalogue definition and is NOT touched:
            // rewriting it would change Poisoned for every crawler in the
            // process, not just this instance.
            { ...record, endsWhen: [{ kind: 'elapsed-time', seconds: POTION_SICKNESS_SECONDS }] }
          : record,
      ),
    },
    line: `Potion Sickness: Poisoned for ${POTION_SICKNESS_SECONDS} seconds (Rules p.49).`,
  };
}

/** Restarts the twenty-second Potion cadence (RULE DCC-POT-004). Only meaningful in a fight. */
export function restartPotionCadence(runtime: RuntimeState): RuntimeState {
  return {
    ...runtime,
    cooldowns: [
      ...runtime.cooldowns.filter((c) => c.sourceId !== startedPotionCadence(0).sourceId),
      startedPotionCadence(runtime.encounter?.round ?? 0),
    ],
  };
}


function hotlistSlotOf(character: Character, itemId: string): number | null {
  const index = character.hotlist.findIndex(
    (slot) => slot !== null && slot.kind === 'item' && slot.itemId === itemId,
  );
  return index === -1 ? null : index;
}

/**
 * What the crawler looks like afterwards, or why they cannot.
 *
 * The order is the order the rules impose, and every step before the first
 * mutation is a preflight: a refusal costs no Action, no copy, no effect and no
 * cadence.
 */
export function useItem(
  character: Character,
  runtime: RuntimeState,
  itemId: string,
  route: UseRoute,
  deps: ItemUseDeps,
  /**
   * The slot the player was looking at, when they were looking at one.
   *
   * Checked against what is THERE NOW. A confirmation dialog can sit open while
   * the Hotlist changes underneath it, and consuming whatever has since arrived
   * in that slot is not what the player agreed to - the same identity lesson
   * E3's `SwapChoice` learned.
   */
  slotIntent: number | null = null,
): ItemUseOutcome {
  // 1. IDENTITY. A Runtime is only evidence about the crawler it names, so a
  // stale one from a screen that switched crawlers must not answer "are you in
  // a fight" about somebody else. Same wording as Inventory and the Hotlist.
  if (runtime.characterId !== character.id) {
    return {
      ok: false,
      reason:
        `Internal error: this Item use is for ${character.id} but the runtime supplied belongs ` +
        `to ${runtime.characterId}. Nothing has been changed. Reopen the crawler and try again.`,
    };
  }

  const item: Item | undefined = deps.catalogue.lookupItem(itemId);
  const slot = hotlistSlotOf(character, itemId);

  if (slotIntent !== null) {
    const entry = character.hotlist[slotIntent] ?? null;
    if (entry === null || entry.kind !== 'item' || entry.itemId !== itemId) {
      return {
        ok: false,
        reason:
          `Hotlist slot ${slotIntent + 1} no longer holds that Item. Nothing has been used - ` +
          'check the slot and try again.',
      };
    }
  }

  // RULE DCC-POT-003 - the hand route's own staleness check. The player agreed
  // to drink the copy in their hand; if it is no longer there - exchanged back
  // into the Hotlist, stowed, or drunk by a mutation that landed first - this
  // refuses and spends nothing rather than reaching for another copy.
  const handEntry = character.inventory.find((e) => e.itemId === itemId);
  if (
    route === 'hand' &&
    (handEntry === undefined ? 0 : inHandCopies(handEntry, deps.catalogue.lookupItem(itemId))) < 1
  ) {
    return {
      ok: false,
      reason:
        'That Item is no longer in the crawler\'s hands. Nothing has been used - check what ' +
        'they are holding and try again.',
    };
  }

  // 2. LEGALITY, from the shared planner. The interface asks the same function,
  // so a button is never offered for a use this would refuse.
  // THE SHARED CONTEXT, re-derived here from the records as they are NOW. The
  // panel and the warning preflight build the same facts through the same
  // function, so they cannot drift; this one is the authoritative reading.
  const plan: ItemUsePlan = planItemUse(
    item,
    runtime,
    physicalUseContext(character, itemId, runtime, inEncounter(runtime), route, slot, item),
  );
  if (!plan.canUse || item === undefined || item.use === undefined) {
    return { ok: false, reason: plan.refusal ?? 'That Item cannot be used.' };
  }

  // 2b. TIMING, THROUGH THE SHARED AUTHORITY, BEFORE ANYTHING IS BUILT.
  // RULE DCC-ENC-003.
  //
  // `planItemUse` deliberately knows nothing about combat phases - it answers
  // "is this Item usable by this crawler at all", and the phase is not a
  // property of the Item. So the phase question is asked here, at the point the
  // service commits to building an Action.
  //
  // WHY NOT JUST LET THE FINAL SPEND REFUSE. It did, and the returned refusal
  // protected every persisted record - but only after the copy had been
  // consumed locally, the effect applied, Potion Sickness constructed and
  // `deps.newId()` CALLED. That last one is a stateful dependency: a refused
  // wrong-phase potion burned an ID. A pure function that has to be thrown away
  // to stay correct is not a preflight.
  //
  // Only when the use would actually cost an Action. Drinking outside a fight
  // spends nothing and has no phase to be wrong about.
  if (plan.spendsAction) {
    const problem = encounterActionProblem(runtime, character.id, 1, 'normal');
    if (problem !== null) return { ok: false, reason: problem };
  }

  const lines: string[] = [];
  let next = character;
  let nextRuntime = runtime;

  // 3. CONSUMPTION, BEFORE THE EFFECT. Exactly one physical copy, and the Hotlist allocation moves
  // with it so `equipped + held + hotlisted <= owned` still holds afterwards. No other
  // slot is touched, nothing is unequipped, and an unresolved entry elsewhere
  // is left exactly as it was.
  //
  // THE ORDER MATTERS, and it used to be the other way round. A custom potion
  // may legitimately be a Hands/Holding Item AND carry modifiers - including
  // +Max Mana or +Intelligence, which Max Mana derives from. Such a potion is
  // mechanically ACTIVE while it sits in the crawler's hand, because a hand is
  // its Gear Slot. Restoring "to the maximum" before removing it therefore
  // filled the crawler up to a maximum that was about to disappear, leaving
  // Current Mana above Max and breaking RULE DCC-MANA-003.
  //
  // Consuming first makes the effect resolve against the crawler who will
  // actually exist: fully restored to their Max Mana AFTER the Item is gone.
  next = {
    ...next,
    inventory: next.inventory
      .map((entry) =>
        entry.itemId === itemId
          ? {
              ...entry,
              quantity: entry.quantity - 1,
              // RULE DCC-POT-003 - the hand route drinks the copy that is IN A
              // HAND, so the hand empties by one as ownership does. WHICH count
              // that is depends on the Item, not on the route's name: a
              // Hands/Holding potion is in the crawler's fist through
              // `equipped`, a slot-less one through `held`. Reading `held`
              // unconditionally made a custom Hands potion undrinkable while
              // being held.
              //
              // The other two routes touch neither: they never took a copy from
              // a hand, and decrementing would empty one still in use.
              ...(route === 'hand'
                ? { [handAllocationField(item)]: entry[handAllocationField(item)] - 1 }
                : {}),
            }
          : entry,
      )
      .filter((entry) => entry.quantity > 0),
  };
  if (route === 'hotlist' && slot !== null) {
    const entry = character.hotlist[slot];
    next = {
      ...next,
      hotlist: next.hotlist.map((existing, index) => {
        if (index !== slot || entry === null || entry === undefined || entry.kind !== 'item') {
          return existing;
        }
        // An emptied slot becomes null rather than a zero-quantity entry: zero
        // is not a valid allocation and would fail structural validation.
        return entry.quantity <= 1
          ? null
          : { kind: 'item', itemId: entry.itemId, quantity: entry.quantity - 1 };
      }),
    };
  }

  // 4. THE EFFECT, against the POST-CONSUMPTION crawler. Exhaustive on the
  // closed union, so adding an arm breaks here
  // rather than silently doing nothing.
  switch (item.use.kind) {
    case 'restore-mana-full': {
      // C&C p.13 - a Standard Mana Potion fully restores Mana. To the EFFECTIVE
      // maximum, computed by the existing derived stats: the Max Mana rule is
      // not restated here, because a second copy of it is a second thing to
      // keep right.
      // `next`, NOT `character`: the consumed copy is already gone, so this is
      // the maximum the crawler will actually have. Still the one shared
      // `deriveStats` - the Max Mana rule is not restated here, because a
      // second copy of it is a second thing to keep right.
      const derived = deriveStats(next, deps.effects, deps.catalogue.lookupItem);
      const restored = derived.mana.max - character.currentMana;
      next = { ...next, currentMana: derived.mana.max };
      lines.push(
        restored > 0
          ? `${item.name} restored ${restored} Mana (now ${derived.mana.max}/${derived.mana.max}).`
          : `${item.name} used at full Mana - nothing was restored.`,
      );
      break;
    }
  }

  // 5. POTION SICKNESS. PROJECT RULING - SOURCE SILENT: the potion still worked
  // and was still consumed above; this is the consequence on top.
  if (plan.causesPotionSickness) {
    const sick = inflictPotionSickness(nextRuntime, deps);
    if (!sick.ok) return { ok: false, reason: sick.reason };
    nextRuntime = sick.runtime;
    lines.push(sick.line);
  }

  // 6. THE ACTION, through the shared pathway so `after-next-action` expiry
  // fires exactly as it does for every other Action. Never by decrementing
  // `actionsRemaining` here.
  if (plan.spendsAction) {
    const spent = spendEncounterAction(nextRuntime, character.id, 1, 'normal');
    if (!spent.ok) return { ok: false, reason: spent.reason };
    nextRuntime = spent.runtime;
    for (const id of spent.expiredEffectIds) lines.push(`${id} ended with that Action`);

    // 7. THE CADENCE, started only in a fight, because Rules p.49 scopes the
    // limit to combat.
    nextRuntime = {
      ...nextRuntime,
      cooldowns: [
        ...nextRuntime.cooldowns.filter((c) => c.sourceId !== startedPotionCadence(0).sourceId),
        startedPotionCadence(nextRuntime.encounter?.round ?? 0),
      ],
    };
  }

  return { ok: true, character: next, runtime: nextRuntime, lines, potionSickness: plan.causesPotionSickness };
}
